import { useNavigate } from "react-router-dom";
import { useModalStore } from "../../../lib/store/useModalStore";
import { useLoginInfo } from "../../../hooks/useLoginInfo.ts";
import { LOGIN_DIALOG_LABEL } from "../../../lib/constants/common.constants.ts";
import { useDeviceStore } from "../../../lib/store/useDeviceStore.tsx";
import { getZodiacImg } from "../../../lib/utils/birthdayUtils.ts";
import { useMyInfoStore } from "../../../lib/store/useMyInfoStore.tsx";
import { useAccessStore } from "../../../lib/store/useAccessStore.tsx";
import { cn } from "../../../lib/utils/cn.ts";
import { useQuery } from "@tanstack/react-query";
import { useNewAlarm } from "../../../@apis/hooks/useSetting.ts";
import Button from "../button/index.tsx";
import { useHydratedAccess } from "../../../hooks/useHydratedAccess.tsx";

/** className merge */
// function cn(...xs: Array<string | false | undefined>) {
//   return xs.filter(Boolean).join(" ");
// }

type AppBarProps = {
  title?: string;
  transparent?: boolean;
  height?: number; // content height (safe-area 제외)
  className?: string;
  search?: boolean;
  alarm?: boolean;
  setting?: boolean;
};

export default function MainAppBar({
  title,
  transparent = false,
  height = 60,
  search = true,
  alarm = true,
  setting = true,
  className,
}: AppBarProps) {
  const { openModal } = useModalStore();
  const { isLoggedIn } = useLoginInfo();
  const hydrated = useHydratedAccess();
  const accessToken = useAccessStore((s) => s.access.accessToken);
  const hasSession = hydrated && !!accessToken;
  const { deviceType } = useDeviceStore();
  const navigate = useNavigate();

  const handleHome = () => {
    navigate("/");
  };

  const { myInfo } = useMyInfoStore();
  const { data: newAlarmResponse } = useQuery({
    ...useNewAlarm(),
    enabled: isLoggedIn,
  });
  const hasNewAlarm = !!newAlarmResponse?.data;

  return (
    <header
      className={cn(
        "sticky top-0 z-40",
        "bg-white backdrop-blur",
        // !transparent && (elevated ? "shadow-sm" : "shadow-none"),
        className,
      )}
      style={{
        // 전체 헤더 높이 = content height + safe-area
        height: `calc(${height}px + var(--safe-area-inset-top))`,
        paddingTop: "var(--safe-area-inset-top)",
      }}
      role="banner"
    >
      <div
        className="mx-auto w-full px-4"
        style={{
          height, // 실제 컨텐츠 라인 높이
          display: "grid",
          gridTemplateColumns: "auto 1fr auto", // 좌: 내용만큼, 가운데: 확장, 우: 내용만큼
          gap: "8px",
        }}
      >
        {/* Left: back or logo */}
        <div className="flex items-center">
          {title ? (
            <h1 className="line-clamp-1 text-[16px] font-bold text-black text-center">
              {title}
            </h1>
          ) : (
            <button
              aria-label="홈"
              onClick={handleHome}
              className="h-full hover:bg-black/5 active:bg-black/10 focus:bg-inherit grid place-items-center"
            >
              <img
                className="h-[24px] w-[32px] object-contain"
                src={"/icons/ic-bi.webp"}
                alt="BI"
                width={81}
                height={60}
              />
            </button>
          )}
        </div>

        {/* Center: title (정중앙) */}
        <div className="flex items-center justify-center min-w-0">
          <div className="w-[100px] h-full" />
        </div>

        {/* Right: actions (끝으로 밀착) */}
        <div className="flex items-center justify-end gap-[15px]">
          {search && (
            <button
              aria-label="검색"
              className="grid place-items-center rounded-xl hover:bg-black/5 active:bg-black/10"
              onClick={() => navigate("/search")}
            >
              <img
                className="size-[24px]"
                src={"/icons/ic-search.webp"}
                alt="search"
              />
            </button>
          )}
          {alarm && (
            <button
              aria-label="알림"
              className="relative grid place-items-center rounded-xl hover:bg-black/5 active:bg-black/10"
              onClick={() =>
                isLoggedIn
                  ? navigate("/alarm")
                  : openModal("CONFIRM_DIALOG", {
                      title: LOGIN_DIALOG_LABEL.title,
                      content: LOGIN_DIALOG_LABEL.content,
                      onClick: () => navigate("/login"),
                    })
              }
            >
              <img
                className="size-[24px]"
                src={"/icons/ic-alarm.webp"}
                alt="alarm"
              />
              {hasNewAlarm && (
                <span className="absolute top-1 right-0 w-[7px] h-[7px] bg-[#e5002b] rounded-full" />
              )}
            </button>
          )}
          {setting && hasSession && (
            <button
              aria-label="설정"
              className="grid place-items-center rounded-xl hover:bg-black/5 active:bg-black/10"
              onClick={() =>
                deviceType !== "android"
                  ? navigate("/setting")
                  : window.open("/setting", "_blank")
              }
            >
              <div className="rounded-full bg-[#f5f5f5] size-[32px] flex items-center justify-center overflow-hidden">
                <img
                  className="size-[32px]"
                  src={getZodiacImg(myInfo.birthdate)}
                  alt="setting"
                />
              </div>
            </button>
          )}
          {setting && hydrated && !accessToken && (
            <Button
              onClick={() => navigate("/login")}
              className="w-fit font-bold h-[56px] text-[18px]"
              variant={"primary"}
              label="로그인하기"
            />
          )}
        </div>
      </div>

      {transparent && (
        <div className="h-px bg-gradient-to-r from-black/10 to-transparent" />
      )}
    </header>
  );
}
